
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Save, CheckCircle, XCircle } from 'lucide-react';

const AdminSetDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [set, setSet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [modified, setModified] = useState(false);

    const fetchSet = React.useCallback(async () => {
        const token = localStorage.getItem('paradise_token');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';
        try {
            const res = await fetch(`${API_BASE}/api/sets/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setSet(await res.json());
            } else {
                navigate('/admin/sets');
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        fetchSet();
    }, [fetchSet]);

    const handleDiscardImage = (index) => {
        if (!window.confirm("Remove this image from the set?")) return;

        const newMediaItems = [...set.mediaItems];
        newMediaItems.splice(index, 1);

        setSet(prev => ({ ...prev, mediaItems: newMediaItems }));
        setModified(true);
    };

    const handleSaveChanges = async () => {
        setSaving(true);
        const token = localStorage.getItem('paradise_token');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';

        try {
            const res = await fetch(`${API_BASE}/api/sets/${id}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ mediaItems: set.mediaItems })
            });

            if (res.ok) {
                setModified(false);
                alert('Changes saved successfully');
            }
        } catch (e) {
            console.error(e);
            alert('Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    const handleStatusUpdate = async (newStatus) => {
        if (!window.confirm(`Mark this set as ${newStatus}?`)) return;
        setSaving(true);

        const token = localStorage.getItem('paradise_token');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';

        try {
            const res = await fetch(`${API_BASE}/api/sets/${id}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    status: newStatus,
                    publishedAt: newStatus === 'published' ? new Date().toISOString() : undefined
                })
            });

            if (res.ok) {
                setSet(prev => ({ ...prev, status: newStatus }));
                alert(`Set ${newStatus} successfully`);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div>Loading...</div>;
    if (!set) return <div>Set not found</div>;

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-4">
                    <button onClick={() => navigate('/admin/sets')} className="p-2 hover:bg-gray-200 rounded-full">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold">{set.title}</h1>
                        <div className="flex items-center space-x-2 text-xs text-gray-500 uppercase tracking-widest">
                            <span>{set.status}</span>
                            <span>•</span>
                            <span>{new Date(set.updatedAt).toLocaleDateString()}</span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center space-x-3">
                    {modified && (
                        <button
                            onClick={handleSaveChanges}
                            disabled={saving}
                            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
                        >
                            <Save size={16} />
                            <span>Save Changes</span>
                        </button>
                    )}

                    <button
                        onClick={() => handleStatusUpdate('rejected')}
                        disabled={saving}
                        className="flex items-center space-x-2 bg-white border border-red-200 text-red-600 px-4 py-2 rounded-md text-sm font-bold hover:bg-red-50 disabled:opacity-50"
                    >
                        <XCircle size={16} />
                        <span>Discard Set</span>
                    </button>

                    {set.status !== 'published' && (
                        <button
                            onClick={() => handleStatusUpdate('published')}
                            disabled={saving}
                            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-green-700 disabled:opacity-50"
                        >
                            <CheckCircle size={16} />
                            <span>Approve & Publish</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Set Details */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Description</h3>
                        <p className="text-sm text-gray-600 leading-relaxed max-w-lg">
                            {set.description || 'No description provided.'}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Metadata</h3>
                        <div className="space-y-1 text-sm">
                            <p><span className="text-gray-500">Slug:</span> <span className="font-mono">{set.slug?.current}</span></p>
                            <p><span className="text-gray-500">ID:</span> <span className="font-mono text-xs">{set._id}</span></p>
                            <p><span className="text-gray-500">Media Items:</span> {set.mediaItems?.length || 0}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Image Grid */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-4">Set Content</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {set.mediaItems && set.mediaItems.map((item, idx) => (
                        <div key={idx} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group border border-gray-200 hover:border-red-500 transition-colors">
                            <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-500 bg-gray-50">
                                {item.type === 'image' && (item.variants?.[0] || item.id) ? (
                                    <img
                                        src={item.variants?.[0] || `https://imagedelivery.net/${process.env.REACT_APP_CLOUDFLARE_ACCOUNT_HASH}/${item.id}/public`}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span>{item.type}</span>
                                )}
                            </div>

                            {/* Actions Overlay */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                <button
                                    onClick={() => handleDiscardImage(idx)}
                                    title="Discard Image"
                                    className="bg-white text-red-600 p-2 rounded-full shadow-lg hover:bg-red-50 transform hover:scale-110 transition-all"
                                >
                                    <Trash2 size={20} />
                                </button>
                            </div>

                            {/* Info Badge */}
                            <div className="absolute bottom-1 left-1 bg-black/50 backdrop-blur text-white text-[10px] px-1 rounded uppercase font-bold pointer-events-none">
                                {idx + 1}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AdminSetDetail;
